import { useEffect, useRef } from "react"
import type { MobileStripBandSettings } from "../../mobileStripBandSettings"
import type { StripPlotArea } from "../stripLayout"
import { lerpNumber } from "../interpolate"
import {
  CHOICE_POINT_DOT_FEATHER_PX,
  CHOICE_POINT_DOT_MIN_SCALE,
} from "../plan-fan/planPreviewFan"
import type { RiskBandModel } from "./riskBandModel"

// The "dither" band style: the background distribution rendered per dot
// on a canvas, mirroring the desktop canvas dither. Each lattice dot's
// radius comes from the boundary fade's density ramp (continuous here —
// the SVG slices only quantize the "solid" style), and dots SHRINK near
// the capability line, and near every fan trajectory while the
// choice-point fan is up, so the lines stay readable through the red.

export type DitherFieldInputs = {
  plot: StripPlotArea
  band: MobileStripBandSettings
  model: RiskBandModel
  yForValue: (value: number) => number
  timeForX: (x: number) => number
  // The rendered active line's screen polyline (x-increasing).
  exclusionLine: Array<[number, number]>
  // Screen polylines of the fan lines (empty while the fan is down).
  fanPolylines: Array<Array<[number, number]>>
  choiceBlend: number
  tip: { x: number; y: number; radius: number }
  lineHalfStrokePx: number
}

// No effect dependency array: the field is a pure function of the frame,
// which already changes on every animated-date render; redrawing ~2-4k
// dots is cheap at this size.
export const DitherFieldCanvas = ({
  inputs,
  opacity,
  fadeMs,
}: {
  inputs: DitherFieldInputs
  opacity: number
  fadeMs: number
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  useEffect(() => {
    if (canvasRef.current !== null) drawDitherField(canvasRef.current, inputs)
  })
  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute left-0 top-0"
      style={{
        width: inputs.plot.width,
        height: inputs.plot.height,
        opacity,
        transition: `opacity ${fadeMs}ms ease-in-out`,
      }}
    />
  )
}

const drawDitherField = (canvas: HTMLCanvasElement, inputs: DitherFieldInputs) => {
  const context = prepareCanvas(canvas, inputs.plot.width, inputs.plot.height)
  if (context === null) return
  context.fillStyle = inputs.band.ditherColor
  forEachLatticeDot(inputs, (cx, cy) => paintDot(context, inputs, cx, cy))
}

// Backing store at devicePixelRatio (capped at 3), drawn in CSS px.
const prepareCanvas = (
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
): CanvasRenderingContext2D | null => {
  const context = canvas.getContext("2d")
  if (context === null) return null
  const devicePixels = Math.min(3, window.devicePixelRatio || 1)
  const deviceWidth = Math.round(width * devicePixels)
  const deviceHeight = Math.round(height * devicePixels)
  if (canvas.width !== deviceWidth) canvas.width = deviceWidth
  if (canvas.height !== deviceHeight) canvas.height = deviceHeight
  context.setTransform(devicePixels, 0, 0, devicePixels, 0, 0)
  context.clearRect(0, 0, width, height)
  return context
}

// The halftone lattice: alternate rows shift phase by half a tile when
// staggered — kills the aligned-grid "picnic blanket" read.
const forEachLatticeDot = (
  inputs: DitherFieldInputs,
  paint: (cx: number, cy: number) => void,
) => {
  const tile = inputs.band.ditherTilePx
  for (let row = 0; ; row++) {
    const cy = tile / 2 + row * tile
    if (cy > inputs.plot.height) break
    const xPhase = inputs.band.ditherStagger && row % 2 === 1 ? 0 : tile / 2
    for (let cx = xPhase; cx <= inputs.plot.width; cx += tile) paint(cx, cy)
  }
}

const paintDot = (
  context: CanvasRenderingContext2D,
  inputs: DitherFieldInputs,
  cx: number,
  cy: number,
) => {
  const radius = ditherDotRadiusAt(inputs, cx, cy)
  if (radius < 0.05) return
  if (inputs.band.ditherDotShape === "square") {
    context.fillRect(cx - radius, cy - radius, radius * 2, radius * 2)
  } else {
    context.beginPath()
    context.arc(cx, cy, radius, 0, Math.PI * 2)
    context.fill()
  }
}

const ditherDotRadiusAt = (inputs: DitherFieldInputs, cx: number, cy: number): number => {
  const t = inputs.timeForX(cx)
  const bottomY = inputs.yForValue(inputs.model.minValueAt(t))
  if (!isInsideField(inputs, t, cy, bottomY)) return 0
  const radius = inputs.band.ditherDotRadiusPx * Math.sqrt(densityAt(inputs, t, cy, bottomY))
  return shrinkNearFanLines(inputs, cx, cy, shrinkNearCapabilityLine(inputs, cx, cy, radius))
}

// Pinched-out columns (boundary at the frame ceiling) have no field —
// the same collapse rule as the solid layers.
const isInsideField = (
  inputs: DitherFieldInputs,
  t: number,
  cy: number,
  bottomY: number,
): boolean => {
  const { band, plot } = inputs
  if (band.bandReach === "top" && bottomY <= plot.padding.top + 0.75) return false
  const fillTopY = band.bandReach === "band"
    ? inputs.yForValue(inputs.model.topValueAt(t))
    : 0
  return cy <= bottomY && cy >= fillTopY
}

// Boundary fade: density ramps from the authored minimum right at the
// boundary up to full at the ramp top.
const densityAt = (
  inputs: DitherFieldInputs,
  t: number,
  cy: number,
  bottomY: number,
): number => {
  const { band } = inputs
  if (!band.boundaryFade) return 1
  const rampSpanPx = bottomY - inputs.yForValue(inputs.model.maxValueAt(t))
  const rampFraction = rampSpanPx <= 0
    ? 1
    : Math.min(1, Math.max(0, (bottomY - cy) / rampSpanPx))
  return band.boundaryFadeMinDensity + (1 - band.boundaryFadeMinDensity) * rampFraction
}

// Dots shrink linearly toward lineDotRadiusMultiplier as they near the
// rendered line + tip dot (the desktop getPolylineDotRadiusMultiplier
// formula: influence = dot radius + exclusion padding + half stroke).
const shrinkNearCapabilityLine = (
  inputs: DitherFieldInputs,
  cx: number,
  cy: number,
  radius: number,
): number => {
  const minScale = inputs.band.lineDotRadiusMultiplier
  if (minScale >= 1) return radius
  const influence = radius + inputs.band.lineDotExclusionPaddingPx + inputs.lineHalfStrokePx
  const tipDistance = Math.max(
    0,
    Math.hypot(cx - inputs.tip.x, cy - inputs.tip.y) - inputs.tip.radius,
  )
  const distance = Math.min(
    distanceToPolyline(cx, cy, inputs.exclusionLine, influence),
    tipDistance,
  )
  if (distance >= influence) return radius
  return radius * (1 - (1 - distance / influence) * (1 - minScale))
}

// Choice-point legibility: dots shrink toward the NEAREST fan trajectory
// on the wide feather ramp — in place of fading the whole field — and
// ride choiceBlend so the gradient eases in/out on the fan's own clock.
const shrinkNearFanLines = (
  inputs: DitherFieldInputs,
  cx: number,
  cy: number,
  radius: number,
): number => {
  if (inputs.choiceBlend <= 0 || inputs.fanPolylines.length === 0) return radius
  let nearest = Number.POSITIVE_INFINITY
  for (const polyline of inputs.fanPolylines) {
    const distance = distanceToPolyline(cx, cy, polyline, CHOICE_POINT_DOT_FEATHER_PX)
    if (distance < nearest) nearest = distance
  }
  const proximityScale = CHOICE_POINT_DOT_MIN_SCALE
    + (1 - CHOICE_POINT_DOT_MIN_SCALE)
      * Math.min(1, nearest / CHOICE_POINT_DOT_FEATHER_PX)
  return radius * lerpNumber(1, proximityScale, inputs.choiceBlend)
}

// Distance to a polyline whose segments are x-increasing, so segments
// outside the influence window x-reject fast; Infinity beyond it —
// callers clamp against influence anyway.
const distanceToPolyline = (
  cx: number,
  cy: number,
  polyline: Array<[number, number]>,
  influence: number,
): number => {
  let bestSquared = Number.POSITIVE_INFINITY
  for (let i = 0; i < polyline.length - 1; i++) {
    const [ax, ay] = polyline[i]
    const [bx, by] = polyline[i + 1]
    if (Math.max(ax, bx) < cx - influence) continue
    if (Math.min(ax, bx) > cx + influence) break
    const dSquared = distanceSquaredToSegment(cx, cy, ax, ay, bx, by)
    if (dSquared < bestSquared) bestSquared = dSquared
  }
  return Math.sqrt(bestSquared)
}

const distanceSquaredToSegment = (
  px: number, py: number,
  ax: number, ay: number,
  bx: number, by: number,
): number => {
  const abx = bx - ax
  const aby = by - ay
  const lengthSquared = abx * abx + aby * aby
  const t = lengthSquared === 0
    ? 0
    : Math.min(1, Math.max(0, ((px - ax) * abx + (py - ay) * aby) / lengthSquared))
  const dx = px - (ax + abx * t)
  const dy = py - (ay + aby * t)
  return dx * dx + dy * dy
}
